import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../../firebase";
import { financialDashboardApi } from "../../../financialDashboard/api/financialDashboardApi";
import { isFinancialTenantScopedActive } from "../../../financialDashboard/auth/FinancialTenantContext";
import {
  CLAIM_CFO_QUEUE_STATUSES,
  CLAIM_PENDING_QUEUE_STATUSES,
  CLAIM_PROCESSED_STATUSES,
  CLAIM_REVIEW_QUEUE_STATUSES,
  CLAIM_STATUS,
  normalizeClaimWorkflowStatus,
} from "./claims/domain/claimWorkflow";
import {
  FINANCIAL_ROLE_GROUPS,
  applyClaimsAccessOverrides,
  buildPlatformFinancialCapabilities,
  canOnboardSupplier,
  FINANCIAL_PLATFORM_ROLES,
  UNITY_FINANCIAL_CLAIM_ROLES,
  normalizePlatformRole,
} from "../../../financialDashboard/financialRoleCapabilities";

export { FINANCIAL_ROLE_GROUPS };

const accessCache = new Map();
const ACCESS_CACHE_TTL_MS = 30 * 1000;

const normalizeRoleTokens = (value) => {
  if (Array.isArray(value)) {
    return value
      .flatMap((entry) => String(entry || "").split(","))
      .map((entry) => entry.trim())
      .filter(Boolean);
  }
  if (typeof value === "string") {
    return value
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean);
  }
  return [];
};

const ALL_FINANCIAL_PRODUCT_ROLES = new Set(FINANCIAL_PLATFORM_ROLES);

const splitPlatformFinancialRoleTokens = (roleValue) => {
  const tokens = normalizeRoleTokens(roleValue);
  const financialRoles = [];
  const unityClaimRoles = [];
  for (const token of tokens) {
    const role = normalizePlatformRole(token);
    if (ALL_FINANCIAL_PRODUCT_ROLES.has(role)) {
      financialRoles.push(role);
    } else if (UNITY_FINANCIAL_CLAIM_ROLES.includes(role)) {
      unityClaimRoles.push(role);
    }
  }
  return { financialRoles, unityClaimRoles };
};

const buildTenantFinancialCapabilities = (roleValue) => {
  const { financialRoles, unityClaimRoles } = splitPlatformFinancialRoleTokens(roleValue);
  return buildPlatformFinancialCapabilities({ financialRoles, unityClaimRoles });
};

const normalizeRole = (value) => String(value || "").trim().toLowerCase();

const hasAnyRole = (roles, allowedRoles = []) => {
  const roleSet = new Set(roles.map(normalizeRole));
  return allowedRoles.some((role) => roleSet.has(normalizeRole(role)));
};

const buildAccessCacheKey = ({ email, company, collectionBrand }) =>
  [String(email || "").trim().toLowerCase(), company, collectionBrand].join("::");

/** Match HeroDashboardContainer: try active brand, then shared. */
const getUserDocBrandCandidates = (collectionBrand) =>
  Array.from(
    new Set(
      [collectionBrand, "shared"]
        .map((value) => String(value || "").trim())
        .filter(Boolean),
    ),
  );

const CAPABILITY_FLAG_KEYS = [
  "canAccessFinancialDashboard",
  "canCreateClaim",
  "canPerformPendingClaimReview",
  "canPerformSecondClaimReview",
  "canApproveRejectClaim",
  "canAccessClaimsCfoApproval",
  "canViewAllClaimHistory",
  "canProcessClaimPayment",
  "canAccessClaimsAuditLog",
  "canAccessClaimsFinancialDashboard",
  "canAccessClaimsSettings",
  "canAccessRecurringPayments",
  "canEditRecurringPayments",
  "canViewRecurringOverdueKpis",
];

/** Prefer fetched flags, but never drop true capabilities already known from Redux/login. */
const mergeFinancialCapabilities = (primary = {}, fallback = {}) => {
  const merged = { ...fallback, ...primary };
  const roles = Array.from(
    new Set([...(fallback.roles || []), ...(primary.roles || [])].filter(Boolean)),
  );
  merged.roles = roles;
  CAPABILITY_FLAG_KEYS.forEach((flag) => {
    merged[flag] = Boolean(primary[flag]) || Boolean(fallback[flag]);
  });
  merged.canPerformManualReview =
    merged.canPerformPendingClaimReview || merged.canPerformSecondClaimReview;
  return merged;
};

export const buildFinancialCapabilities = (roleValue) => {
  const roles = normalizeRoleTokens(roleValue);
  const canAccessClaimsAuditLog = hasAnyRole(
    roles,
    FINANCIAL_ROLE_GROUPS.canAccessClaimsAuditLog,
  );
  const canAccessClaimsFinancialDashboard = hasAnyRole(
    roles,
    FINANCIAL_ROLE_GROUPS.canAccessClaimsFinancialDashboard,
  );
  const canAccessClaimsSettings = hasAnyRole(
    roles,
    FINANCIAL_ROLE_GROUPS.canAccessClaimsSettings,
  );
  const canViewAllClaimHistory = hasAnyRole(
    roles,
    FINANCIAL_ROLE_GROUPS.canViewAllClaimHistory,
  );
  const canPerformPendingClaimReview = hasAnyRole(
    roles,
    FINANCIAL_ROLE_GROUPS.canPerformPendingClaimReview,
  );
  const canPerformSecondClaimReview = hasAnyRole(
    roles,
    FINANCIAL_ROLE_GROUPS.canPerformSecondClaimReview,
  );
  const canApproveRejectClaim = hasAnyRole(
    roles,
    FINANCIAL_ROLE_GROUPS.canApproveRejectClaim,
  );
  const canAccessClaimsCfoApproval = hasAnyRole(
    roles,
    FINANCIAL_ROLE_GROUPS.canAccessClaimsCfoApproval,
  );
  const canProcessClaimPayment = hasAnyRole(
    roles,
    FINANCIAL_ROLE_GROUPS.canProcessClaimPayment,
  );
  const canAccessRecurringPayments = hasAnyRole(
    roles,
    FINANCIAL_ROLE_GROUPS.canAccessRecurringPayments,
  );
  const canEditRecurringPayments = hasAnyRole(
    roles,
    FINANCIAL_ROLE_GROUPS.canEditRecurringPayments,
  );
  return {
    roles,
    canAccessFinancialDashboard: hasAnyRole(
      roles,
      FINANCIAL_ROLE_GROUPS.canAccessFinancialDashboard,
    ),
    canCreateClaim: hasAnyRole(roles, FINANCIAL_ROLE_GROUPS.canCreateClaim),
    canPerformPendingClaimReview,
    canPerformSecondClaimReview,
    canApproveRejectClaim,
    canAccessClaimsCfoApproval,
    canViewAllClaimHistory,
    canProcessClaimPayment,
    canAccessClaimsAuditLog,
    canAccessClaimsFinancialDashboard,
    canAccessClaimsSettings,
    canAccessRecurringPayments,
    canEditRecurringPayments,
    /** Overdue recurring KPIs are CFO-only. */
    canViewRecurringOverdueKpis: canAccessClaimsCfoApproval,
    /** Backward-compatible aggregate flag used by older review UI branches. */
    canPerformManualReview: canPerformPendingClaimReview || canPerformSecondClaimReview,
  };
};

/** EXCO/CFO pending supplier review queues (not legacy Hero Admin). */
export const canAccessPendingSupplierReviews = (access = {}) =>
  Boolean(access.canPerformSecondClaimReview) ||
  Boolean(access.canAccessClaimsCfoApproval);

/** Map legacy financial capabilities to per-claims-view nav/route access flags. */
export const getClaimsViewAccess = (access = {}) => ({
  canAccessClaimsDashboard: Boolean(access.canAccessFinancialDashboard),
  canAccessClaimsNew: Boolean(access.canCreateClaim),
  canAccessClaimsPending:
    Boolean(access.canPerformPendingClaimReview) || Boolean(access.canViewAllClaimHistory),
  canAccessClaimsReview:
    Boolean(access.canPerformSecondClaimReview) || Boolean(access.canViewAllClaimHistory),
  canAccessClaimsProcessed: Boolean(access.canViewAllClaimHistory),
  canAccessClaimsFinancial:
    Boolean(access.canAccessClaimsFinancialDashboard) && Boolean(access.canViewAllClaimHistory),
  canAccessClaimsRecurring: Boolean(access.canAccessRecurringPayments),
  canAccessClaimsCfo: Boolean(access.canAccessClaimsCfoApproval),
  canAccessClaimsAudit: Boolean(access.canAccessClaimsAuditLog),
  canAccessClaimsSettings: Boolean(access.canAccessClaimsSettings),
  canAccessPendingSupplierReviews: canAccessPendingSupplierReviews(access),
  canAccessSuppliers: Boolean(access.canAccessFinancialDashboard),
  canAccessSuppliersAdd: canOnboardSupplier(access.roles || []),
});

/* Hero analytics Claims Spending chart, EXCO/CFO or Financial Dashboard settings permission. */
export const canViewClaimsSpendingChart = (access = {}) =>
  Boolean(access?.canAccessClaimsFinancialDashboard);

/** Tenant and legacy home overview KPI row (value, paid, approved, processing time). */
export const canViewFinancialHomeKpis = (access = {}) =>
  Boolean(access?.canAccessClaimsFinancialDashboard);

/* Hero analytics Claims Awaiting Action card, any claims queue the user can action. */
export const canViewClaimsAwaitingActionCard = (access = {}) =>
  Boolean(
    access?.canPerformPendingClaimReview ||
      access?.canPerformSecondClaimReview ||
      access?.canAccessClaimsCfoApproval ||
      access?.canViewAllClaimHistory,
  );

/** CFO, Manager, and claims-auth grants that unlock all-claim history. */
export const canViewPendingClaimsOverview = (access = {}) =>
  Boolean(
    access?.canViewAllClaimHistory ||
      access?.canAccessClaimsCfoApproval ||
      access?.canPerformSecondClaimReview ||
      access?.canPerformPendingClaimReview,
  );

const CLAIMS_ACCESS_DOC_KEYS = [
  "additionalFinanceAccessByEmail",
  "overridesByEmail",
];

const getClaimAccessOverrideForEmail = (settingsData, email) => {
  const normalizedEmail = String(email || "").trim().toLowerCase();
  if (!normalizedEmail || !settingsData || typeof settingsData !== "object") {
    return {};
  }

  for (const key of CLAIMS_ACCESS_DOC_KEYS) {
    const byEmail = settingsData?.[key];
    if (byEmail && typeof byEmail === "object") {
      const override = byEmail[normalizedEmail];
      if (override && typeof override === "object") {
        return override;
      }
    }
  }
  return {};
};

export const fetchFinancialAccess = async ({
  email,
  company,
  collectionBrand,
  fallbackRoleValue,
} = {}) => {
  const tenantScoped = isFinancialTenantScopedActive();
  const fallbackCapabilities = tenantScoped
    ? buildTenantFinancialCapabilities(fallbackRoleValue ?? [])
    : buildFinancialCapabilities(fallbackRoleValue ?? []);
  const emptyCapabilities = tenantScoped
    ? buildTenantFinancialCapabilities([])
    : buildFinancialCapabilities([]);

  if (isFinancialTenantScopedActive()) {
    try {
      const response = await financialDashboardApi.getAccess();
      return mergeFinancialCapabilities(response?.access || {}, fallbackCapabilities);
    } catch (_error) {
      return fallbackCapabilities;
    }
  }

  if (!email || !company || !collectionBrand) {
    return emptyCapabilities;
  }

  const key = buildAccessCacheKey({ email, company, collectionBrand });
  const cached = accessCache.get(key);
  if (cached) {
    if (
      cached &&
      typeof cached === "object" &&
      Object.prototype.hasOwnProperty.call(cached, "value")
    ) {
      if (Date.now() - Number(cached.timestamp || 0) <= ACCESS_CACHE_TTL_MS) {
        const resolved =
          cached.value instanceof Promise ? await cached.value : cached.value;
        return mergeFinancialCapabilities(resolved, fallbackCapabilities);
      }
      accessCache.delete(key);
    } else {
      const resolved = cached instanceof Promise ? await cached : cached;
      return mergeFinancialCapabilities(resolved, fallbackCapabilities);
    }
  }

  const promise = (async () => {
    const brandCandidates = getUserDocBrandCandidates(collectionBrand);
    let userData = null;
    for (const brand of brandCandidates) {
      const userDocSnap = await getDoc(doc(db, company, brand, "Users", email));
      if (userDocSnap.exists()) {
        userData = userDocSnap.data() || {};
        break;
      }
    }

    const baseCapabilities = userData
      ? buildFinancialCapabilities(userData.role ?? userData.userRole ?? [])
      : emptyCapabilities;

    let override = {};
    for (const brand of brandCandidates) {
      try {
        const settingsSnap = await getDoc(
          doc(db, company, brand, "financialSettings", "claimsAccessSettings"),
        );
        if (!settingsSnap.exists()) continue;
        override = getClaimAccessOverrideForEmail(settingsSnap.data() || {}, email);
        if (override && Object.keys(override).length) break;
      } catch (_error) {
        // Keep trying remaining brand candidates.
      }
    }

    return mergeFinancialCapabilities(
      applyClaimsAccessOverrides(baseCapabilities, override),
      fallbackCapabilities,
    );
  })();

  accessCache.set(key, { timestamp: Date.now(), value: promise });
  try {
    const result = await promise;
    accessCache.set(key, { timestamp: Date.now(), value: result });
    return result;
  } catch (error) {
    accessCache.delete(key);
    throw error;
  }
};

export const useFinancialAccess = () => {
  const { email, company, collectionBrand, userRole } = useSelector(
    (state) => state.heroUser,
  );

  const localRoleCapabilities = useMemo(() => {
    if (isFinancialTenantScopedActive()) {
      return buildTenantFinancialCapabilities(userRole);
    }
    return buildFinancialCapabilities(userRole);
  }, [userRole]);

  const [state, setState] = useState({
    loading: true,
    ...localRoleCapabilities,
  });

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setState((prev) => ({ ...prev, loading: true }));
      try {
        const access = await fetchFinancialAccess({
          email,
          company,
          collectionBrand,
          fallbackRoleValue: userRole,
        });
        if (!mounted) return;
        setState({ loading: false, ...access });
      } catch (_error) {
        if (!mounted) return;
        setState({ loading: false, ...localRoleCapabilities });
      }
    };

    void load();

    return () => {
      mounted = false;
    };
  }, [email, company, collectionBrand, userRole, localRoleCapabilities]);

  return state;
};

export const resetFinancialAccessCache = () => {
  accessCache.clear();
};

export const isClaimVisibleToUser = ({ claim, userEmail, access }) => {
  if (!claim) return false;
  const status = normalizeClaimWorkflowStatus(claim.status);
  if (status === CLAIM_STATUS.DRAFT) {
    return String(claim.createdBy || "").toLowerCase() === String(userEmail || "").toLowerCase();
  }
  if (access?.canViewAllClaimHistory) return true;
  if (CLAIM_PENDING_QUEUE_STATUSES.has(status) && access?.canPerformPendingClaimReview) {
    return true;
  }
  if (CLAIM_REVIEW_QUEUE_STATUSES.has(status) && access?.canPerformSecondClaimReview) {
    return true;
  }
  if (CLAIM_CFO_QUEUE_STATUSES.has(status) && access?.canAccessClaimsCfoApproval) {
    return true;
  }
  if (CLAIM_PROCESSED_STATUSES.has(status)) {
    const createdBy = String(claim.createdBy || "").toLowerCase();
    return createdBy === String(userEmail || "").toLowerCase();
  }
  const createdBy = String(claim.createdBy || "").toLowerCase();
  return createdBy === String(userEmail || "").toLowerCase();
};

/** Submitters can open their own post-submit review; reviewers need elevated roles. */
export const isClaimVisibleForManualReview = ({ claim, userEmail, access }) => {
  if (!claim) return false;
  const status = normalizeClaimWorkflowStatus(claim.status);
  if (status === CLAIM_STATUS.DRAFT) return false;
  if (access?.canViewAllClaimHistory) return true;
  if (status === CLAIM_STATUS.PENDING && access?.canPerformPendingClaimReview) return true;
  if (status === CLAIM_STATUS.REVIEW && access?.canPerformSecondClaimReview) return true;
  const createdBy = String(claim.createdBy || "").toLowerCase();
  return createdBy === String(userEmail || "").toLowerCase();
};
